import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import localeEn from '@angular/common/locales/en';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService
} from 'keycloak-angular';
import { environment } from 'src/environments/environment';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ConfirmationService, MessageService } from 'primeng/api';

import { WebRqaPreset } from './theme.config';
import { routes } from '../routes';
import { loaderInterceptorFn } from '../core/interceptors/loader.interceptor';
import { apiErrorInterceptorFn } from '../core/interceptors/api-error.interceptor';
import { permissionInterceptorFn } from '../core/interceptors/permission.interceptor';
import { CustomFilterService } from '../services/custom-filter.service';
import { DialogService } from 'primeng/dynamicdialog';

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, '/assets/i18n/', '.json?cache=' + new Date().valueOf());

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /.*/,
  bearerPrefix: 'Bearer'
});

registerLocaleData(localePl, 'pl');
registerLocaleData(localeEn, 'en');

export const provideKeycloakAngular = () =>
  provideKeycloak({
    config: {
      url: environment.keycloak.url,
      realm: environment.keycloak.realm,
      clientId: environment.keycloak.clientId
    },
    initOptions: {
      onLoad: 'login-required',
      checkLoginIframe: false,
      enableLogging: true
    },
    providers: [AutoRefreshTokenService, UserActivityService]
  });

type TranslationSetupFunction = () => Promise<void>;

function setupTranslateFactory(service: TranslateService): TranslationSetupFunction {
  return () =>
    service
      .use('pl')
      .toPromise()
      .then(() => {});
}

function setupFilterFactory(filterConfigService: CustomFilterService): () => Promise<void> {
  return (): Promise<void> => {
    return filterConfigService.configureFilterService();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideKeycloakAngular(),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition]
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    providePrimeNG({ theme: { preset: WebRqaPreset, options: { darkModeSelector: '.app-dark' } } }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        includeBearerTokenInterceptor,
        permissionInterceptorFn,
        loaderInterceptorFn,
        apiErrorInterceptorFn
      ])
    ),
    { provide: LOCALE_ID, useValue: 'pl' },
    MessageService,
    DialogService,
    ConfirmationService,
    CustomFilterService,
    // Locale
    { provide: LOCALE_ID, useValue: 'pl' },

    // APP_INITIALIZER functions
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateFactory,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: setupFilterFactory,
      deps: [CustomFilterService],
      multi: true
    }
  ]
};
